/**
 * A block definition in its own view, apart from any design that uses it
 * (D20, requirement 8.7).
 *
 * The same editor as a design: palette, canvas, inspector. What differs is
 * where it saves (the library entity's draft), what it cannot hold (blocks
 * within blocks, D16), and the boundary: which internal ports the block
 * exposes. Publishing a version is what offers every placed instance the
 * update (requirement 8.8); nothing propagates on its own.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Banner, NOTICE_DISMISS_MS } from "../components";
import { ApiError, api, type LibraryDraft, type LibraryEntityDetail } from "../api/client";
import {
  servesKind,
  type DesignKind,
  type ExposedPort,
  type Finding,
  type GroupDefinitionDocument,
} from "../design/document";
import {
  createEditorState,
  editorReducer,
  EditorCanvas,
  fitToBounds,
  hasUnsavedChanges,
  placeFromPalette,
  zoomAbout,
  type EditorHost,
} from "../editor";
import { buildScene, DEFAULT_VIEW } from "../scene";
import { SAVE_DELAY_MS, SaveState, useSize, ViewControls } from "./DesignWorkspace";
import { errorText } from "./blocks";
import { asEditable, definitionContent, exposedName, freePorts, layoutExposed, occupiedExposed } from "./definition";
import { Inspector } from "./Inspector";
import { EMPTY_WORKSPACE_LIBRARY, loadWorkspaceLibrary, swapCandidates, type WorkspaceLibrary } from "./library";
import { PanelTab } from "./PanelTab";
import { Palette } from "./Palette";
import { loadAnnotations, loadGrid, loadPaneOpen, savePaneOpen } from "./preferences";
import { EditorTools } from "./EditorTools";
import "./workspace.css";

type SaveStatus = "idle" | "saving" | "failed";

export interface DefinitionWorkspaceProps {
  entity: LibraryEntityDetail;
  initialDraft: LibraryDraft;
  /** Where the designer came from, to go back to. */
  back?: { to: string; label: string };
  onPublished: () => void;
}

export function DefinitionWorkspace({ entity, initialDraft, back, onPublished }: DefinitionWorkspaceProps) {
  const entityId = entity.id;
  const base = initialDraft.content as unknown as GroupDefinitionDocument;
  const baseRef = useRef(base);
  const [state, dispatch] = useReducer(editorReducer, undefined, () => ({
    ...createEditorState(asEditable(base), { revision: initialDraft.revision }),
    annotations: loadAnnotations(),
    grid: loadGrid(),
  }));
  const stateRef = useRef(state);
  stateRef.current = state;

  // The boundary, held beside the editor. Not pruned when a component goes,
  // so undoing the deletion brings its exposed port back too.
  const [exposed, setExposed] = useState<ExposedPort[]>(base.exposed_ports ?? []);
  const [summary, setSummary] = useState(base.summary ?? "");
  const [savedMeta, setSavedMeta] = useState({ exposed: base.exposed_ports ?? [], summary: base.summary ?? "" });
  // The inspector beside the canvas folds away to its tab, as it does in a design.
  const [inspectorOpen, setInspectorOpen] = useState(() => loadPaneOpen("definition"));
  const toggleInspector = () => {
    const next = !inspectorOpen;
    setInspectorOpen(next);
    savePaneOpen("definition", next);
  };

  const [library, setLibrary] = useState<WorkspaceLibrary>(EMPTY_WORKSPACE_LIBRARY);
  const [libraryLoading, setLibraryLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [refusal, setRefusal] = useState<Finding[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [latest, setLatest] = useState(entity.latest_version_number ?? null);
  // Whether what is saved differs from the latest version. Designs using the
  // block see only published versions (D16), so this is what the view has to
  // make plain: saved here is not yet anywhere else.
  const [pending, setPending] = useState(false);
  const refreshPending = useCallback(async () => {
    try {
      const diff = await api.libraryDraftDiff(entityId);
      setPending(diff !== null && diff.entries.length > 0);
    } catch {
      // Advisory only.
    }
  }, [entityId]);
  useEffect(() => {
    void refreshPending();
  }, [refreshPending]);
  const savingRef = useRef(false);
  const refusedRef = useRef<unknown>(null);
  const [canvasBox, size] = useSize<HTMLDivElement>();

  useEffect(() => {
    let cancelled = false;
    // A library block may only use library templates: a project's own would
    // not resolve in another project that placed it.
    loadWorkspaceLibrary(entity.project_id ?? null, stateRef.current.document)
      .then((loaded) => {
        if (cancelled) return;
        setLibrary(loaded);
        dispatch({ type: "LIBRARY_LOADED", library: loaded.scene });
      })
      .catch((error: unknown) => !cancelled && setProblem(`The library could not be loaded: ${errorText(error)}`))
      .finally(() => !cancelled && setLibraryLoading(false));
    return () => {
      cancelled = true;
    };
  }, [entity.project_id]);

  const framed = useRef(false);
  useEffect(() => {
    if (framed.current || libraryLoading || size.width < 100) return;
    framed.current = true;
    if ((state.document.nodes ?? []).length === 0) return;
    const bounds = buildScene(state.document, state.library, [], DEFAULT_VIEW).bounds;
    dispatch({ type: "SET_VIEWPORT", viewport: fitToBounds(bounds, size.width, size.height) });
  }, [size, libraryLoading, state.document, state.library]);

  const metaChanged = exposed !== savedMeta.exposed || summary !== savedMeta.summary;
  const unsaved = hasUnsavedChanges(state) || metaChanged;
  const content = useMemo(
    () => definitionContent(baseRef.current, state.document, exposed, summary),
    [state.document, exposed, summary],
  );

  const save = useCallback(async (): Promise<boolean> => {
    const current = stateRef.current;
    if (savingRef.current || current.revision === null) return false;
    const document = current.document;
    const sent = { exposed, summary };
    const body = definitionContent(baseRef.current, document, exposed, summary);
    savingRef.current = true;
    setSaveStatus("saving");
    try {
      const saved = await api.updateLibraryDraft(entityId, body as unknown as Record<string, unknown>, current.revision);
      dispatch({ type: "DRAFT_SAVED", document, revision: saved.revision });
      setSavedMeta(sent);
      void refreshPending();
      setRefusal([]);
      refusedRef.current = null;
      setSaveStatus("idle");
      setProblem(null);
      return true;
    } catch (error) {
      setSaveStatus("failed");
      if (error instanceof ApiError && error.isConflict) {
        setConflict(true);
      } else if (error instanceof ApiError && error.findings.length > 0) {
        refusedRef.current = body;
        setRefusal(error.findings);
      } else {
        setProblem(`Saving failed: ${errorText(error)}`);
      }
      return false;
    } finally {
      savingRef.current = false;
    }
  }, [entityId, exposed, summary, refreshPending]);

  // Refused content is not retried until it changes: `content` is memoised,
  // so the same edit state is the same object.
  useEffect(() => {
    if (!unsaved || state.drag || conflict || content === refusedRef.current) return;
    const timer = window.setTimeout(() => void save(), SAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [unsaved, state.drag, conflict, content, save]);
  useEffect(() => {
    if (refusedRef.current !== null && content !== refusedRef.current) refusedRef.current = null;
  }, [content]);

  useEffect(() => {
    if (!unsaved) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsaved]);

  const publish = async () => {
    setPublishing(true);
    setNotice(null);
    try {
      if (unsaved && !(await save())) return;
      const version = await api.publishLibraryVersion(entityId, message.trim());
      // Publishing moves the draft's revision on.
      const draft = await api.getLibraryDraft(entityId);
      dispatch({ type: "DRAFT_SAVED", document: stateRef.current.savedDocument, revision: draft.revision });
      setLatest(version.number);
      setPending(false);
      setMessage("");
      setNotice(`Published version ${version.number}. Designs using an earlier version are offered the update.`);
      onPublished();
    } catch (error) {
      setProblem(`Publishing failed: ${errorText(error)}`);
    } finally {
      setPublishing(false);
    }
  };

  // --- The boundary ------------------------------------------------------------

  const expose = (ref: string) => {
    const next = [...exposed, { name: exposedName(ref, exposed.map((port) => port.name)), internal_ref: ref }];
    setExposed(layoutExposed(state.document, next));
  };
  const conceal = (port: ExposedPort) =>
    setExposed(layoutExposed(state.document, exposed.filter((candidate) => candidate !== port)));
  const renameExposed = (port: ExposedPort, name: string) =>
    setExposed(exposed.map((candidate) => (candidate === port ? { ...candidate, name } : candidate)));

  const available = freePorts(state.document).filter((ref) => !exposed.some((port) => port.internal_ref === ref));
  const occupied = new Set(occupiedExposed(state.document, exposed));
  const present = new Set(
    (state.document.nodes ?? []).flatMap((node) => (node.ports ?? []).map((port) => `${node.local_id}.${port.local_id}`)),
  );
  const duplicateNames = new Set(
    exposed.map((port) => port.name).filter((name, index, names) => names.indexOf(name) !== index),
  );

  const sceneFor = () => buildScene(state.document, state.library, [], { ...DEFAULT_VIEW, collapsed: state.collapsed });
  // A block has no kind of its own; it takes the kind of what it is built
  // from, so a tubing-set block is edited with the tubing-set palette (D34).
  const kind: DesignKind = (state.document.nodes ?? []).some((node) => {
    const template = node.template_version_id ? library.scene.templates[node.template_version_id] : undefined;
    return template !== undefined && !servesKind(template, "microfluidic") && servesKind(template, "tubing_set");
  })
    ? "tubing_set"
    : "microfluidic";
  const host: EditorHost = useMemo(
    () => ({
      inspect: (ref) => dispatch({ type: "SELECT", refs: [ref] }),
      swapCandidates: (ref) => {
        const node = state.document.nodes?.find((candidate) => candidate.local_id === ref);
        return node ? swapCandidates(library, node.role, node.template_version_id, kind) : [];
      },
    }),
    [library, kind, state.document],
  );
  const place = (versionId: string) =>
    dispatch(placeFromPalette(state, sceneFor(), versionId, size.width, size.height));
  const centre = { x: size.width / 2, y: size.height / 2 };
  const busy = publishing || saveStatus === "saving";

  return (
    <div className="ws">
      <header className="ws-header">
        <div className="ws-title">
          {back && (
            <Link to={back.to} className="ws-back">
              ← {back.label}
            </Link>
          )}
          <strong>{entity.name}</strong>
          <span className="ws-muted ws-subtitle">
            block · {entity.is_library_scoped ? "shared library" : "project"} ·{" "}
            {latest === null ? "never published" : `v${latest} published`}
          </span>
        </div>
        <div className="ws-header-actions">
          <SaveState unsaved={unsaved} status={saveStatus} conflict={conflict} />
          <input
            className="ws-message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Version message"
            aria-label="Version message"
          />
          <button
            type="button"
            className="primary"
            onClick={() => void publish()}
            disabled={busy || conflict || refusal.length > 0}
          >
            Publish v{(latest ?? 0) + 1}
          </button>
        </div>
      </header>

      {latest !== null && (pending || unsaved) && (
        <div className="ws-notice ws-pending" role="status">
          <strong>Not published yet.</strong> Designs using this block still have v{latest}. Publish v{latest + 1} to
          offer them the update; each design then updates when its designer chooses to.
        </div>
      )}
      {conflict && (
        <div className="ws-conflict" role="alert">
          <strong>Someone else saved this block.</strong> Your changes are still here and have not been saved. Reload to
          see theirs; nothing has been merged.
        </div>
      )}
      {problem && (
        <Banner className="error" role="alert" onDismiss={() => setProblem(null)}>
          {problem}
        </Banner>
      )}
      {refusal.length > 0 && (
        <div className="error" role="alert">
          The definition could not be saved: {refusal.map((finding) => finding.message).join("; ")}
        </div>
      )}
      {notice && (
        <Banner className="ws-notice" autoDismissMs={NOTICE_DISMISS_MS} resetKey={notice} onDismiss={() => setNotice(null)}>
          {notice}
        </Banner>
      )}

      <div className={`ws-body${inspectorOpen ? "" : " ws-body-inspector-closed"}`}>
        <Palette library={library} kind={kind} loading={libraryLoading} onPlace={place} />
        <main className="ws-main">
          <div ref={canvasBox} className="ws-canvas">
            <EditorCanvas
              state={state}
              dispatch={dispatch}
              width={size.width}
              height={size.height}
              host={host}
              title={`${entity.name} block definition`}
            />
            <ViewControls
              tools={<EditorTools state={state} dispatch={dispatch} />}
              zoom={state.viewport.zoom}
              canFit={(state.document.nodes ?? []).length > 0}
              annotations={state.annotations}
              onAnnotations={(change) => dispatch({ type: "SET_ANNOTATIONS", ...change })}
              onZoom={(factor) => dispatch({ type: "SET_VIEWPORT", viewport: zoomAbout(state.viewport, factor, centre) })}
              onActualSize={() =>
                dispatch({ type: "SET_VIEWPORT", viewport: zoomAbout(state.viewport, 1 / state.viewport.zoom, centre) })
              }
              onFit={() => dispatch({ type: "SET_VIEWPORT", viewport: fitToBounds(sceneFor().bounds, size.width, size.height) })}
            />
          </div>
        </main>
        <div className="ws-inspector-slot">
          <PanelTab side="right" open={inspectorOpen} name="inspector" onToggle={toggleInspector} />
          {inspectorOpen && (
            <>
              <section className="ws-boundary" aria-label="Block boundary">
                <h3>Summary</h3>
                <input
                  value={summary}
                  onChange={(event) => setSummary(event.target.value)}
                  aria-label="Summary"
                  style={{ width: "100%" }}
                />
                <h3>Exposed ports</h3>
                <p className="ws-muted ws-small">
                  What a placed instance shows on its collapsed symbol, and the only ports lines outside it can use.
                </p>
                {exposed.length === 0 && <p className="ws-muted ws-small">None: nothing can connect to this block.</p>}
                <ul className="ws-ports">
                  {exposed.map((port) => (
                    <li key={`${port.internal_ref}-${port.name}`}>
                      <input
                        value={port.name}
                        aria-label={`Name of exposed port ${port.internal_ref}`}
                        onChange={(event) => renameExposed(port, event.target.value)}
                        className="ws-narrow-name"
                      />{" "}
                      <code>{port.internal_ref}</code>
                      {!present.has(port.internal_ref) && <span className="ws-muted"> · component deleted</span>}
                      {occupied.has(port.name) && <span className="ws-muted"> · connected inside, so unusable outside</span>}
                      {duplicateNames.has(port.name) && <span className="ws-muted"> · name used twice</span>}{" "}
                      <button type="button" className="ws-link" onClick={() => conceal(port)}>
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
                {available.length > 0 && (
                  <>
                    <h3>Free ports</h3>
                    <ul className="ws-ports">
                      {available.map((ref) => (
                        <li key={ref}>
                          <code>{ref}</code>{" "}
                          <button type="button" className="ws-link" onClick={() => expose(ref)}>
                            Expose
                          </button>
                        </li>
                      ))}
                    </ul>
                </>
              )}
              </section>
              <Inspector state={state} library={library} findings={[]} dispatch={dispatch} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
